import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import axios from 'axios'
import { Hub } from 'aws-amplify/utils'
import { AmplifyErrorCode } from '@aws-amplify/core/internals/utils'
import { logger } from 'services/logger'
import { isClient } from 'utils/runtimeUtils'
import {
  confirmEmailCode as cognitoConfirmEmailCode,
  fetchIdToken as cognitoFetchIdToken,
  getAccessToken,
  getUser,
  initCognito,
  initiateEmailSignIn as cognitoInitiateEmailSignIn,
  signInWithApple as cognitoSignInWithApple,
  signInWithGoogle as cognitoSignInWithGoogle,
  signOut as cognitoSignOut,
} from './cognito'
import { getBrazeInstance } from '../Analytics/platforms/braze.client'
import useOnMount from '../../hooks/useOnMount'
import {
  COGNITO_NOT_AUTHORIZED_ERROR_NAME,
  type CognitoUser,
} from './cognito.types'
import { useKeys } from '../KeysProvider'

// Served by the sign-out-session route
const SIGN_OUT_SESSION_PATH = '/sign-out-session'

interface AuthContextType {
  isAuthenticated: boolean
  user: CognitoUser | undefined // Use the simplified CognitoUser type
  isLoading: boolean
  initiateEmailSignIn: (email: string) => Promise<any> // Returns Cognito challenge data
  confirmEmailCode: (userChallenge: any, code: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithApple: () => Promise<void>
  getAccessToken: () => Promise<string | null>
  getIdToken: () => Promise<string | null>
  logout: (returnToUrl?: string) => Promise<void>
  error: any | null
}

interface AuthProviderProps {
  children: React.ReactNode
  // Runs on every sign-out, the automatic ones too, before the page is left
  onSignOut: () => void
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: undefined,
  isLoading: true,
  initiateEmailSignIn: async () => {
    throw new Error('AuthProvider not initialized')
  },
  confirmEmailCode: async () => {
    throw new Error('AuthProvider not initialized')
  },
  signInWithGoogle: async () => {
    throw new Error('AuthProvider not initialized')
  },
  signInWithApple: async () => {
    throw new Error('AuthProvider not initialized')
  },
  getAccessToken: async () => null,
  getIdToken: async () => null,
  logout: async () => {},
  error: null,
})

function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthContextProvider')
  }

  return context
}

async function signOutAndNavigate(
  onSignOut: () => void,
  navigateAfterSignOut: () => void
) {
  // Clear Braze user identity before signing out to prevent
  // accidentally modifying the logged-in user's subscription status
  const braze = getBrazeInstance(false)
  if (braze) {
    braze.wipeData()
  }
  onSignOut()

  await cognitoSignOut()
  if (isClient()) {
    await endBrowserSession()
    navigateAfterSignOut()
  }
}

// A failure keeps the old session, and with it the account's identity and photos in this browser; sign-out goes on
// regardless
async function endBrowserSession() {
  try {
    await axios.post(SIGN_OUT_SESSION_PATH)
  } catch (err) {
    logger.error('Failed to end the browser session on sign out', err)
  }
}

function reloadPage() {
  // Assigning the current URL to location.href only scrolls to the fragment when the
  // URL has one, so reload to land back on the same page as a guest
  window.location.reload()
}

function logTokenRefreshFailure(
  refreshError: Error | undefined,
  loginExpiredBeforePageLoad: boolean
) {
  // Cognito rejects an expired or revoked refresh token with NotAuthorizedException, which
  // is routine session expiry; any other failure (throttling, 5xx) is an outage signal
  if (refreshError?.name === COGNITO_NOT_AUTHORIZED_ERROR_NAME) {
    logger.warning('Token refresh rejected, session expired', {
      errorName: refreshError.name,
      errorMessage: refreshError.message,
      loginExpiredBeforePageLoad,
    })
  } else {
    logger.error('Token refresh failed', refreshError, {
      errorName: refreshError?.name,
    })
  }
}

function AuthProvider({ children, onSignOut }: AuthProviderProps) {
  const [user, setUser] = useState<CognitoUser | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<any | null>(null)
  // Read by the Hub listener and getIdToken; set together with user so a refresh failure
  // or token read that lands before the next render still sees the signed-in user
  const isAuthenticatedRef = useRef(false)
  const keys = useKeys()

  useOnMount(() => initCognito(keys))

  const checkUser = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const cognitoUser: CognitoUser = await getUser()
      isAuthenticatedRef.current = true
      setUser(cognitoUser)
    } catch (err) {
      isAuthenticatedRef.current = false
      setUser(undefined)
      if (
        err instanceof Error &&
        !err.message.includes('User needs to be authenticated to call this API')
      ) {
        logger.error('Error fetching user on load', err)
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    checkUser()
  }, [checkUser])

  const logoutPromiseRef = useRef<Promise<void> | null>(null)

  const signOutOnce = useCallback(
    (navigateAfterSignOut: () => void) => {
      if (logoutPromiseRef.current) {
        return logoutPromiseRef.current
      }

      setIsLoading(true)
      setError(null)
      const logoutPromise = signOutAndNavigate(
        onSignOut,
        navigateAfterSignOut
      ).catch((err) => {
        logger.error('Sign out failed', err)
        setError(err)
        setIsLoading(false)
        logoutPromiseRef.current = null
      })
      logoutPromiseRef.current = logoutPromise
      return logoutPromise
    },
    [onSignOut]
  )

  const logout = useCallback(
    (returnToUrl?: string) =>
      signOutOnce(() => {
        window.location.href = returnToUrl ?? `${window.location.origin}/`
      }),
    [signOutOnce]
  )

  const isAuthenticated = isAuthenticatedRef.current

  const getIdToken = useCallback(async () => {
    let idToken: string | null
    try {
      idToken = await cognitoFetchIdToken()
    } catch (err) {
      // A refresh that failed on a network error keeps the stored tokens; every other
      // refresh failure also reaches the tokenRefresh_failure handler below
      logger.info('No session for getIdToken', { err })
      return null
    }

    // Tokens removed with no refresh attempt (sign-out in another tab, cleared storage)
    // fire no Hub event, so this is the only place that notices them
    if (!idToken && isAuthenticatedRef.current && !logoutPromiseRef.current) {
      logger.warning('Signed-in user has no Cognito tokens, signing out')
      signOutOnce(reloadPage)
    }
    return idToken
  }, [signOutOnce])

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      logger.info('user event', { event: payload.event })
      switch (payload.event) {
        case 'signInWithRedirect':
          setIsLoading(true)
          break
        case 'signInWithRedirect_failure':
          setError(payload.data)
          setIsLoading(false)
          break
        case 'signedIn':
        case 'tokenRefresh':
          checkUser()
          break
        case 'tokenRefresh_failure': {
          const refreshError = payload.data.error
          // Amplify clears the stored tokens on every refresh failure except a network error
          if (
            refreshError?.name === AmplifyErrorCode.NetworkError ||
            logoutPromiseRef.current
          ) {
            break
          }
          logTokenRefreshFailure(refreshError, !isAuthenticatedRef.current)
          // A session that expired before this page load leaves a guest with nothing to
          // reset; only a user signed in on this page needs the sign-out and reload
          if (isAuthenticatedRef.current) {
            signOutOnce(reloadPage)
          }
          break
        }
        case 'signedOut':
          // since we reload after sign out, we don't need to set user to undefined here
          // this actually causes a bug in TilesProvider, since api.isAuthenticated changes
          // causing a tiles persistence side-effect to run, so we avoid setUser(undefined).
          // setUser(undefined)
          setIsLoading(false)
          break
        default:
          break
      }
    })

    return () => unsubscribe()
  }, [checkUser, signOutOnce])

  const initiateEmailSignIn = useCallback(async (email: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await cognitoInitiateEmailSignIn(email)
      setIsLoading(false)
      return result
    } catch (err) {
      logger.error('Email sign in initiation failed', err)
      setError(err)
      setIsLoading(false)
      throw err
    }
  }, [])

  const confirmEmailCode = useCallback(
    async (userChallenge: any, code: string) => {
      setIsLoading(true)
      setError(null)
      try {
        await cognitoConfirmEmailCode(userChallenge, code)
      } catch (error) {
        logger.warning('Email code confirmation failed', { error })
        setError(error)
        setIsLoading(false)
        throw error
      }
    },
    []
  )

  const signInWithGoogle = useCallback(async () => {
    setError(null)
    try {
      await cognitoSignInWithGoogle()
    } catch (err) {
      logger.error('Google sign in failed', err)
      setError(err)
    }
  }, [])

  const signInWithApple = useCallback(async () => {
    setError(null)
    try {
      await cognitoSignInWithApple()
    } catch (err) {
      logger.error('Apple sign in failed', err)
      setError(err)
    }
  }, [])

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      user,
      isLoading,
      initiateEmailSignIn,
      confirmEmailCode,
      signInWithGoogle,
      signInWithApple,
      getAccessToken,
      getIdToken,
      logout,
      error,
    }),
    [
      isAuthenticated,
      user,
      isLoading,
      error,
      initiateEmailSignIn,
      confirmEmailCode,
      signInWithGoogle,
      signInWithApple,
      getIdToken,
      logout,
    ]
  )

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export { AuthProvider, useAuth }
